import { useEffect, useRef } from "react";
import { useFaqs, localizeFaq } from "@/hooks/useFaqs";
import AboutSubNav from "@/components/AboutSubNav";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getAboutT } from "@/i18n/about";
import { Link } from "@/lib/router-compat";
import { motion, useInView } from "framer-motion";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import {
  RefreshCw,
  GraduationCap,
  Globe,
  Lightbulb,
  Award,
  Puzzle,
  ShieldCheck,
  ArrowRight,
  Smile,
  Handshake,
  Rocket,
  Target,
  Briefcase,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import LatestNews from "@/components/LatestNews";
import Footer from "@/components/Footer";
import aboutTeam from "@/assets/about-team.jpg";
import aboutMission from "@/assets/about-mission.jpg";
import aboutEcosystem from "@/assets/about-ecosystem.png";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ───────── animated counter ───────── */
const AnimatedStat = ({
  value,
  label,
  suffix = "+",
}: {
  value: number;
  label: string;
  suffix?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const countRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!inView || !countRef.current) return;
    let start = 0;
    const end = value;
    const duration = 1600;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        start = end;
        clearInterval(timer);
      }
      if (countRef.current) countRef.current.textContent = start.toLocaleString();
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <div ref={ref} className="text-center">
      <p className="text-4xl sm:text-5xl font-extrabold text-primary-foreground">
        <span ref={countRef}>0</span>
        {suffix}
      </p>
      <p className="mt-2 text-sm sm:text-base font-medium text-primary-foreground/70">
        {label}
      </p>
    </div>
  );
};

/* ───────── differentiator card ───────── */
const DifferentiatorCard = ({
  icon: Icon,
  title,
  description,
  index,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  index: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.45, delay: index * 0.08 }}
    whileHover={{ y: -5, scale: 1.02 }}
    className="group relative rounded-2xl border border-border bg-card p-7 shadow-sm transition-shadow hover:shadow-lg hover:shadow-accent/10"
  >
    <div className="mb-5 text-accent">
      <Icon size={28} />
    </div>
    <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
    <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
  </motion.div>
);

/* ───────── value card ───────── */
const ValueCard = ({
  title,
  description,
  bg,
  circle,
  icon: Icon,
  index,
}: {
  title: string;
  description: string;
  bg: string;
  circle: string;
  icon: React.ElementType;
  index: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    className="relative overflow-hidden rounded-2xl border border-border/60 p-7 min-h-[180px] flex flex-col justify-end"
    style={{ backgroundColor: bg }}
  >
    {/* decorative overlapping circles */}
    <div
      className="absolute -top-10 -left-10 h-40 w-40 rounded-full opacity-50"
      style={{ backgroundColor: circle }}
    />
    <div
      className="absolute -top-6 left-8 h-36 w-36 rounded-full opacity-40"
      style={{ backgroundColor: circle }}
    />
    <Icon size={28} className="text-primary/60 relative z-10 mb-3" />
    <h3 className="text-xl font-bold text-primary relative z-10">{title}</h3>
    <p className="text-sm leading-relaxed text-primary/70 relative z-10 mt-2">{description}</p>
  </motion.div>
);

/* ───────── certification badge ───────── */
const CertBadge = ({ name, entity }: { name: string; entity: string }) => (
  <div className="flex items-start gap-4 rounded-xl border border-border/60 bg-card p-5">
    <ShieldCheck size={28} className="shrink-0 text-accent mt-0.5" />
    <div>
      <p className="font-semibold text-foreground">{name}</p>
      <p className="text-sm text-muted-foreground">{entity}</p>
    </div>
  </div>
);

/* ═══════════════════ MAIN PAGE ═══════════════════ */
const About = () => {
  const lang = useLanguage();
  const t = getAboutT(lang);
  const prefix = langPrefix(lang);

  const stats = [
    { value: 11, label: t.stats[0].label },
    { value: 700, label: t.stats[1].label },
    { value: 1200, label: t.stats[2].label },
    { value: 4500, label: t.stats[3].label },
  ];

  const diffIcons = [RefreshCw, GraduationCap, Globe, Lightbulb, Award, Puzzle];
  const differentiators = t.differentiators.items.map((d, i) => ({
    icon: diffIcons[i],
    title: d.title,
    description: d.description,
  }));

  const valueBgs = [
    { bg: "#F0D4E0", circle: "#D4A0BB", icon: Smile },
    { bg: "#E2E8A0", circle: "#C8D25C", icon: Handshake },
    { bg: "#C8D0E8", circle: "#9AA8D0", icon: Rocket },
    { bg: "#A8E0D0", circle: "#5CC8A8", icon: Target },
  ];
  const values = t.values.items.map((v, i) => ({
    title: v.title,
    description: v.description,
    ...valueBgs[i],
  }));

  const certifications = [
    { name: "ISO 13485:2016", entity: "Qarad" },
    { name: "ISO/IEC 27001:2022", entity: "Qarad" },
    { name: "GMP License", entity: "Quercus" },
    { name: "EUROTOX", entity: "Azierta" },
  ];

  const faqs = t.faqs;

  const { data: dbFaqs = [] } = useFaqs("about", "about-qbd");
  const localizedDbFaqs = dbFaqs.map((f) => localizeFaq(f, lang));
  const activeFaqs = localizedDbFaqs.length > 0 ? localizedDbFaqs : faqs;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: activeFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        canonical={`https://qbdgroup.com${lang === "es" ? "/es/sobre-qbd" : "/en/about-qbd"}`}
        ogImage={aboutTeam}
      >
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </SEOHead>
      <BreadcrumbJsonLd items={[{ name: t.hero.kicker }]} />

      <ScrollToTop />
      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-primary pt-36 sm:pt-44 pb-20 sm:pb-28 min-h-[420px] sm:min-h-[480px]">
          {/* subtle pattern */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] bg-[length:32px_32px]" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
              {/* text */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-3xl sm:text-5xl lg:text-[50px] font-bold tracking-tight text-primary-foreground leading-[1.1]">
                  {lang === "en" && t.hero.title.includes("QbD Group") ? (
                    <>About <span className="whitespace-nowrap">QbD Group</span></>
                  ) : t.hero.title}
                </h1>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-primary-foreground/75 max-w-lg">
                  {t.hero.p1}
                </p>
                <p className="mt-4 text-base leading-relaxed text-primary-foreground/65 max-w-lg">
                  {t.hero.p2}
                </p>
              </motion.div>

              {/* image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="relative"
              >
                <img
                  src={aboutTeam}
                  alt="QbD Group leadership team"
                  className="w-full rounded-2xl object-cover shadow-2xl shadow-black/30 max-h-[520px]"
                />
              </motion.div>
            </div>
          </div>
        </section>

        <AboutSubNav />

        {/* ── STATS BAR ── */}
        <section className="bg-primary border-t border-primary-foreground/10">
          <div className="mx-auto max-w-5xl px-4 py-12 sm:py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <AnimatedStat key={s.label} value={s.value} label={s.label} />
            ))}
          </div>
        </section>

        {/* ── MISSION & VISION ── */}
        <section className="py-20 sm:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={aboutMission}
                alt="QbD Group office"
                className="rounded-2xl object-cover shadow-lg w-full max-h-[440px]"
              />
            </motion.div>

            <div className="space-y-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                  {t.mission.title}
                </p>
                <p className="text-lg leading-relaxed text-foreground">
                  {t.mission.text}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                  {t.vision.title}
                </p>
                <p className="text-lg leading-relaxed text-foreground">
                  {t.vision.text}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-6 rounded-xl bg-secondary/60 border border-border/40"
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                  {t.objectives.title}
                </p>
                <ul className="space-y-2 text-muted-foreground text-sm leading-relaxed">
                  {t.objectives.items.map((item, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-accent mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── KEY DIFFERENTIATORS ── */}
        <section className="py-20 sm:py-28 bg-muted/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-14"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {t.differentiators.kicker}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">
                {t.differentiators.title}
              </h2>
              <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
                {t.differentiators.subtitle}
              </p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {differentiators.map((d, i) => (
                <DifferentiatorCard key={d.title} index={i} {...d} />
              ))}
            </div>
          </div>
        </section>

        {/* ── JPEG VALUES ── */}
        <section className="py-20 sm:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-14"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {t.values.kicker}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">
                {t.values.title}
              </h2>
              <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
                {t.values.subtitle}
              </p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v, i) => (
                <ValueCard key={v.title} index={i} {...v} />
              ))}
            </div>

            {/* Careers CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12 text-center"
            >
              <div className="inline-flex items-center gap-4 rounded-2xl border border-border bg-card px-8 py-6 shadow-sm">
                <Briefcase size={24} className="text-accent shrink-0" />
                <div className="text-left">
                  <p className="font-semibold text-foreground">{lang === "es" ? "¿Te interesa trabajar en QbD Group?" : lang === "zh-cn" ? "有兴趣加入 QbD Group？" : "Interested in working at QbD Group?"}</p>
                  <a
                    href="https://careers.qbdgroup.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors mt-1"
                  >
                    {lang === "es" ? "Descubre nuestras vacantes" : lang === "zh-cn" ? "查看空缺职位" : "Explore our open positions"} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── ECOSYSTEM ── */}
        <section className="py-20 sm:py-28 bg-muted/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {t.ecosystem.kicker}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-6">
                {t.ecosystem.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t.ecosystem.p1}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t.ecosystem.p2}
              </p>
              <Link
                to={lang === "es" ? "/es/sobre-qbd/ecosistema" : lang === "zh-cn" ? "/zh-cn/about-qbd/ecosystem" : "/en/about-qbd/ecosystem"}
                className="group inline-flex items-center gap-2 mt-6 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
              >
                {t.ecosystem.cta}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={aboutEcosystem}
                alt="QbD Group ecosystem of partners"
                className="rounded-2xl object-cover shadow-lg w-full"
              />
            </motion.div>
          </div>
        </section>

        {/* ── FOUNDATION TEASER ── */}
        <section className="py-20 sm:py-28 bg-primary">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {t.foundation.kicker}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-primary-foreground mb-4">
                {t.foundation.title}
              </h2>
              <p className="text-primary-foreground/70 max-w-2xl mx-auto leading-relaxed mb-6">
                {t.foundation.text}
              </p>
              <Link
                to={lang === "es" ? "/es/sobre-qbd/fundacion" : lang === "zh-cn" ? "/zh-cn/about-qbd/qbd-group-foundation" : "/en/about-qbd/qbd-group-foundation"}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
              >
                {t.foundation.cta}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ── LICENSES & CERTIFICATIONS ── */}
        <section className="py-20 sm:py-28 bg-background">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {t.certifications.kicker}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">
                {t.certifications.title}
              </h2>
            </motion.div>

            <div className="grid gap-4 sm:grid-cols-2">
              {certifications.map((c) => (
                <CertBadge key={c.name} {...c} />
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-20 sm:py-28 bg-muted/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">
                {({ en: "Capabilities and global reach", es: "Capacidades y alcance global", "zh-cn": "专业能力与全球布局" }[lang])}
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">
                {t.faqTitle}
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Accordion type="single" collapsible className="space-y-3">
                {activeFaqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="rounded-xl border border-border/60 bg-card px-6 shadow-sm data-[state=open]:shadow-md transition-shadow"
                  >
                    <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 [&_a]:text-accent [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-accent/80">
                      <span dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>

        {/* ── CONTINUE EXPLORING ── */}
        <section className="py-16 sm:py-20 bg-background border-t border-border/40">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-6 text-center">
              {lang === "es" ? "Sigue descubriendo" : lang === "zh-cn" ? "继续探索" : "Continue exploring"}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { label: lang === "es" ? "Nuestra historia" : lang === "zh-cn" ? "我们的故事" : "Our story", href: lang === "es" ? "/es/sobre-qbd/historia" : lang === "zh-cn" ? "/zh-cn/about-qbd/story" : "/en/about-qbd/story", desc: lang === "es" ? "Descubre cómo empezó todo" : lang === "zh-cn" ? "了解一切是如何开始的" : "Discover how it all started" },
                { label: lang === "es" ? "Nuestro equipo" : lang === "zh-cn" ? "我们的团队" : "Our team", href: lang === "es" ? "/es/sobre-qbd/equipo" : lang === "zh-cn" ? "/zh-cn/about-qbd/team" : "/en/about-qbd/team", desc: lang === "es" ? "Conoce a las personas detrás de QbD" : lang === "zh-cn" ? "认识 QbD 背后的团队成员" : "Meet the people behind QbD" },
                { label: lang === "es" ? "Ecosistema" : lang === "zh-cn" ? "生态系统" : "Ecosystem", href: lang === "es" ? "/es/sobre-qbd/ecosistema" : lang === "zh-cn" ? "/zh-cn/about-qbd/ecosystem" : "/en/about-qbd/ecosystem", desc: lang === "es" ? "Nuestras alianzas estratégicas" : lang === "zh-cn" ? "我们的战略合作伙伴关系" : "Our strategic partnerships" },
              ].map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="group flex items-center gap-4 rounded-xl border border-border bg-card p-5 hover:shadow-md hover:border-accent/30 transition-all duration-200"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-foreground group-hover:text-accent transition-colors">{item.label}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── LATEST NEWS ── */}
        <LatestNews />
      </main>

      <Footer />
    </>
  );
};

export default About;
